import type {
  ApiError,
  ApiToken,
  ApiTokenCreated,
  AuditEntry,
  AuthResult,
  Chunk,
  Conversation,
  FlatEvent,
  Invitation,
  LoginRequest,
  LoginSession,
  Me,
  Member,
  MfaChallenge,
  MfaStatus,
  AddressLookupResult,
  MfaVerifyRequest,
  OrgProfile,
  PostalAddress,
  OrgSettingsPatch,
  Organization,
  Page,
  Passkey,
  Project,
  ProjectFull,
  ProjectPatch,
  Role,
  SessionActivity,
  SessionDetail,
  SessionReview,
  SessionSummary,
  SignupRequest,
  SsoConnection,
  SsoConnectionCreate,
  User,
  VisitDetail,
} from "@backstory/protocol";
import { getCookie } from "./lib/cookies";
import { trackPending } from "./lib/pending";

declare global {
  interface Window {
    __BACKSTORY_CONFIG__?: { apiUrl?: string; ingestUrl?: string; apiToken?: string; devTokenAuth?: boolean; turnstileSiteKey?: string; recorderKey?: string };
  }
}
const runtime = typeof window !== "undefined" ? window.__BACKSTORY_CONFIG__ : undefined;
export const API_URL: string = (runtime?.apiUrl || (import.meta.env.VITE_API_URL as string | undefined) || "http://localhost:8081").replace(/\/$/, "");

/** Where a site sends visits. Local development uses the gateway; a hosted page can set ingestUrl in its config. */
export function ingestUrl(): string {
  const configured = runtime?.ingestUrl || (import.meta.env.VITE_INGEST_URL as string | undefined);
  if (configured) return configured.replace(/\/$/, "");
  if (typeof location !== "undefined" && (location.hostname === "localhost" || location.hostname === "127.0.0.1")) return "http://localhost:8080/v1/ingest";
  return `${API_URL}/v1/ingest`;
}

/**
 * Dev-token auth is a local convenience for running against a backend without
 * the auth module. It is only honored when VITE_DEV_TOKEN_AUTH=true (or the
 * runtime config sets devTokenAuth). Production builds use cookie sessions.
 */
export const DEV_TOKEN_AUTH: boolean = runtime?.devTokenAuth === true || (import.meta.env.VITE_DEV_TOKEN_AUTH as string | undefined) === "true";
export const DEV_TOKEN: string | undefined = DEV_TOKEN_AUTH ? runtime?.apiToken || (import.meta.env.VITE_API_TOKEN as string | undefined) || "backstory_dev_token" : undefined;

/**
 * Signing out has to outlive a reload. The dev bearer token below is only used
 * when no cookie principal exists, which is exactly the state logout creates,
 * so without this latch a sign-out silently re-authenticates as the dev
 * principal on the next navigation. Cleared when a real principal appears.
 */
const LS_SIGNED_OUT = "backstory-signed-out";

export function devAuthSuppressed(): boolean {
  if (!DEV_TOKEN_AUTH) return false;
  try {
    return localStorage.getItem(LS_SIGNED_OUT) === "1";
  } catch {
    return false;
  }
}

export function setDevAuthSuppressed(suppressed: boolean): void {
  try {
    if (suppressed) localStorage.setItem(LS_SIGNED_OUT, "1");
    else localStorage.removeItem(LS_SIGNED_OUT);
  } catch {
    /* ignore */
  }
}

export const CSRF_COOKIE = "backstory_csrf";
export const CSRF_HEADER = "X-Backstory-CSRF";

// The session cookie is set by the API host. A page on another host cannot
// read it, so the sign-in and profile responses also carry the token.
let csrfMemory: string | undefined;

export function rememberCsrf(token: string | undefined): void {
  if (token) csrfMemory = token;
}

export function clearCsrf(): void {
  csrfMemory = undefined;
}

export function currentCsrf(): string | undefined {
  return getCookie(CSRF_COOKIE) || csrfMemory;
}
export const PROJECT_HEADER = "X-Backstory-Project";
export const ENVIRONMENT_HEADER = "X-Backstory-Environment";
export const ORG_HEADER = "X-Backstory-Org";
export const UNAUTHORIZED_EVENT = "backstory:unauthorized";
export const MFA_REQUIRED_EVENT = "backstory:mfa-required";
export const SUBSCRIPTION_EXPIRED_EVENT = "backstory:subscription-expired";

/** The project scope sent with session endpoints for cookie principals. Set by the auth store. */
let currentProjectId: string | undefined;
export function setApiProject(id: string | undefined): void {
  currentProjectId = id;
}
export function getApiProject(): string | undefined {
  return currentProjectId;
}

/** Optional analytical environment scope. An absent value means all environments. */
let currentEnvironment: string | undefined;
export function setApiEnvironment(environment: string | undefined): void {
  currentEnvironment = environment;
}
export function getApiEnvironment(): string | undefined {
  return currentEnvironment;
}

/** Org scope for SaaS staff acting through a support-access grant. */
let currentOrgHeader: string | undefined;
export function setApiOrg(id: string | undefined): void {
  currentOrgHeader = id;
}
export function getApiOrg(): string | undefined {
  return currentOrgHeader;
}

/** Read-only datastore probe from GET /setup/status. Never includes secrets. */
export interface SetupHealthCheck {
  name: string;
  ok: boolean;
  required: boolean;
  latencyMs: number;
  error?: string;
}

export interface SetupStatus {
  setupRequired: boolean;
  /** False when this install is invite-only. Absent on an older api, which the signup page treats as open. */
  signupOpen?: boolean;
  ready?: boolean;
  health?: SetupHealthCheck[];
}

/** Mail settings collected by the setup wizard. Secrets are write-only. */
export interface SetupMail {
  provider: "log" | "resend" | "smtp" | "ses";
  from: string;
  resendApiKey?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpTls?: "starttls" | "implicit" | "none";
}

export interface SetupCompleteRequest {
  code: string;
  email: string;
  password: string;
  name?: string;
  orgName: string;
  orgProfile?: OrgProfile;
  projectName?: string;
  mode: "self_hosted" | "saas";
  allowPublicSignup: boolean;
  mail: SetupMail;
}

export class ApiRequestError extends Error {
  /** What went wrong in technical terms. Logged, never shown on a screen. */
  public detail?: string;
  constructor(
    public status: number,
    public code: ApiError["code"] | "network",
    message: string,
    public body?: unknown,
    detail?: string,
  ) {
    super(message);
    this.detail = detail;
  }
}

/** The browser could not complete the request at all. */
export function networkError(e: unknown): ApiRequestError {
  const raw = (e as Error)?.message ?? String(e);
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  return new ApiRequestError(
    0,
    "network",
    offline ? "You appear to be offline. Check your connection and try again." : "We could not connect. Check your connection and try again.",
    undefined,
    `${API_URL}: ${raw}`,
  );
}

/**
 * Turn a failed response into an error whose message a person can read. The
 * server's own wording is kept when it wrote one for people (4xx); anything
 * else becomes a plain sentence, and the raw text goes on `detail`.
 */
export function responseError(res: Response, body: Record<string, unknown>): ApiRequestError {
  const code = (typeof body.code === "string" ? body.code : "internal") as ApiError["code"];
  const raw = typeof body.error === "string" ? body.error : "";
  let message: string;
  if (res.status >= 500 || code === "internal" || raw === "" || raw === "internal" || raw === "internal error") {
    message =
      res.status === 429
        ? "Too many attempts. Wait a few minutes and try again."
        : res.status === 404
          ? "That could not be found."
          : res.status >= 500
            ? "Something went wrong on our side. Try again in a moment."
            : "That request could not be completed. Try again.";
  } else {
    message = raw.endsWith(".") || raw.endsWith("?") ? raw : raw.charAt(0).toUpperCase() + raw.slice(1) + ".";
  }
  return new ApiRequestError(res.status, code, message, body, raw || `${res.status} ${res.statusText}`);
}

interface ReqOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** Suppress the global unauthorized event (auth probes). */
  quiet401?: boolean;
  /** Absolute URL instead of API_URL + path. */
  absolute?: boolean;
  /** Capability-token request: never attach app auth, tenancy headers, or cookies. */
  public?: boolean;
  /** Read this project instead of the one selected in the header. */
  projectId?: string;
  /** Ignore the environment selected in the header. */
  allEnvironments?: boolean;
}

export function request<T>(path: string, opts: ReqOptions = {}): Promise<T> {
  const work = send<T>(path, opts);
  return (opts.method ?? "GET") === "GET" ? work : trackPending(work);
}

async function send<T>(path: string, opts: ReqOptions): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json", ...(opts.headers ?? {}) };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && !opts.public) {
    const csrf = currentCsrf();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  const project = opts.projectId || currentProjectId;
  if (!opts.public && project) headers[PROJECT_HEADER] = project;
  if (!opts.public && currentEnvironment && !opts.allEnvironments) headers[ENVIRONMENT_HEADER] = currentEnvironment;
  if (!opts.public && currentOrgHeader) headers[ORG_HEADER] = currentOrgHeader;
  // backstory_session is HttpOnly; the CSRF cookie is set with it and is readable.
  // Prefer the logged-in cookie principal over the local dev Bearer token, and
  // never fall back to the token once the user has explicitly signed out.
  if (!opts.public && DEV_TOKEN && !currentCsrf() && !devAuthSuppressed()) headers.Authorization = `Bearer ${DEV_TOKEN}`;

  let res: Response;
  try {
    res = await fetch(opts.absolute ? path : `${API_URL}${path}`, {
      method,
      headers,
      credentials: opts.public ? "omit" : "include",
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw networkError(e);
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error */
    }
    if (res.status === 401 && !opts.public && !opts.quiet401 && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(UNAUTHORIZED_EVENT, {
          detail: {
            path,
            code: typeof body.code === "string" ? body.code : undefined,
            message: typeof body.error === "string" ? body.error : undefined,
          },
        }),
      );
    }
    // The API locks every product route behind MFA enrollment.
    // Tell the auth store so the app swaps to the enrollment gate instead of
    // rendering pages full of "mfa_required" errors.
    if (res.status === 403 && body.code === "mfa_required" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(MFA_REQUIRED_EVENT, { detail: { path } }));
    }
    if (res.status === 402 && body.code === "subscription_expired" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_EXPIRED_EVENT, { detail: { path } }));
    }
    throw responseError(res, body);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function authHeaders(method: string): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") {
    const csrf = currentCsrf();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  if (currentProjectId) headers[PROJECT_HEADER] = currentProjectId;
  if (currentEnvironment) headers[ENVIRONMENT_HEADER] = currentEnvironment;
  if (currentOrgHeader) headers[ORG_HEADER] = currentOrgHeader;
  if (DEV_TOKEN && !currentCsrf() && !devAuthSuppressed()) headers.Authorization = `Bearer ${DEV_TOKEN}`;
  return headers;
}

/** Multipart upload. The browser sets the multipart boundary. */
export function requestForm<T>(path: string, form: FormData): Promise<T> {
  return trackPending(sendForm<T>(path, form));
}

async function sendForm<T>(path: string, form: FormData): Promise<T> {
  const headers = authHeaders("POST");
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { method: "POST", headers, credentials: "include", body: form });
  } catch (e) {
    throw networkError(e);
  }
  if (!res.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch { /* non-JSON */ }
    throw responseError(res, body);
  }
  return (await res.json()) as T;
}

/** Authenticated binary read, used for private images. */
export async function requestBlob(path: string): Promise<Blob> {
  const headers = authHeaders("GET");
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { headers, credentials: "include" });
  } catch (e) {
    throw networkError(e);
  }
  if (!res.ok) throw responseError(res, {});
  return res.blob();
}

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Sessions (existing contract)
// ---------------------------------------------------------------------------

export interface SessionsQuery {
  q?: string;
  hasErrors?: boolean;
  hasGaps?: boolean;
  browser?: string;
  os?: string;
  device?: string;
  level?: string;
  minDurationSec?: number;
  maxDurationSec?: number;
  from?: string;
  to?: string;
  user?: string;
  urlContains?: string;
  release?: string;
  reviewed?: "reviewed" | "unreviewed";
  viewed?: "viewed" | "unviewed";
  flag?: string;
  flagValue?: string;
  country?: string;
  limit?: number;
  cursor?: string;
  /** Read this project's visits instead of the one selected in the header. */
  projectId?: string;
}

export interface ShareLink {
  id: string;
  url: string;
  token: string;
  expiresAt: string;
}
export interface SessionShareLink {
  id: string;
  sessionId: string;
  projectId: string;
  startMs: number;
  expiresAt: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  revokedAt?: string;
  views: number;
}

/** Curated payload exposed by a public capability token. */
export interface SharedSession {
  startedAt: string;
  lastEventAt: string;
  endedAt?: string;
  durationMs: number;
  url: string;
  title?: string;
  browser?: string;
  os?: string;
  device?: string;
  viewport: SessionDetail["viewport"];
  privacyMode: SessionDetail["privacyMode"];
  recordingMode: SessionDetail["recordingMode"];
  eventCount: number;
  errorCount: number;
  chunks: SessionDetail["chunks"];
  gaps: SessionDetail["gaps"];
  startMs: number;
  expiresAt: string;
}
export interface SharedConversationSummary {
  id: string;
  createdAt: string;
  updatedAt: string;
  preview: string;
}
export interface ExportJob {
  id: string;
  status: "queued" | "running" | "ready" | "failed";
  downloadUrl?: string;
  error?: string;
}

/** A browser key. The full key is shown once, when it is made; after that only its start is known. */
export interface BrowserKey {
  id: string;
  projectId: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt?: string;
}

export interface NewBrowserKey {
  key: BrowserKey;
  secret: string;
}

export interface ProjectInstall {
  session?: { id: string; url: string; title?: string; startedAt: string; events?: number };
  signal?: { id: string; title: string };
  /** Why a visit has not been saved, when the key's recording settings explain it. */
  check?: "recording_off" | "triggers_only" | "sample_none" | "empty_visit";
  notice?: { code: string; origin?: string; at: string };
}

export const api = {
  project: () => request<Project>("/v1/projects/current"),
  environments: () => request<{ items: string[] }>("/v1/environments").then((response) => response.items),

  sessions: (q: SessionsQuery = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set("q", q.q);
    if (q.hasErrors) p.set("hasErrors", "true");
    if (q.hasGaps) p.set("hasGaps", "true");
    if (q.browser) p.set("browser", q.browser);
    if (q.os) p.set("os", q.os);
    if (q.device) p.set("device", q.device);
    if (q.level) p.set("level", q.level);
    if (q.minDurationSec != null) p.set("minDurationSec", String(q.minDurationSec));
    if (q.maxDurationSec != null) p.set("maxDurationSec", String(q.maxDurationSec));
    if (q.from) p.set("from", q.from);
    if (q.to) p.set("to", q.to);
    if (q.user) p.set("user", q.user);
    if (q.urlContains) p.set("urlContains", q.urlContains);
    if (q.release) p.set("release", q.release);
    if (q.reviewed) p.set("reviewed", q.reviewed);
    if (q.viewed) p.set("viewed", q.viewed);
    if (q.flag) p.set("flag", q.flag);
    if (q.flagValue) p.set("flagValue", q.flagValue);
    if (q.country) p.set("country", q.country);
    p.set("limit", String(q.limit ?? 50));
    if (q.cursor) p.set("cursor", q.cursor);
    return request<Page<SessionSummary>>(`/v1/sessions?${p.toString()}`, q.projectId ? { projectId: q.projectId, allEnvironments: true } : {});
  },
  session: (id: string) => request<SessionDetail>(`/v1/sessions/${enc(id)}`),
  reviewSession: (id: string, reviewed: boolean) => request<SessionReview>(`/v1/sessions/${enc(id)}/review`, { method: "PATCH", body: { reviewed } }),
  deleteSession: (id: string) => request<Deletion>(`/v1/sessions/${enc(id)}`, { method: "DELETE" }),
  projectSessionCount: (projectId: string, w?: { from?: string; to?: string }) => request<{ count: number }>(`/v1/projects/${enc(projectId)}/sessions${windowQuery(w)}`, { allEnvironments: true }),
  deleteProjectSessions: (projectId: string, w?: { from?: string; to?: string }) => request<Deletion>(`/v1/projects/${enc(projectId)}/sessions${windowQuery(w)}`, { method: "DELETE", allEnvironments: true }),
  deleteBackendEvents: (projectId: string, w?: { from?: string; to?: string }) => request<Deletion>(`/v1/projects/${enc(projectId)}/backend-events${windowQuery(w)}`, { method: "DELETE", allEnvironments: true }),
  sessionActivity: (id: string) => request<{ items: SessionActivity[] }>(`/v1/sessions/${enc(id)}/activity`),
  chunk: (id: string, seq: number) => request<Chunk>(`/v1/sessions/${enc(id)}/chunks/${seq}`),
  events: (id: string, types?: string[], limit = 2000) => {
    const p = new URLSearchParams();
    if (types?.length) p.set("types", types.join(","));
    p.set("limit", String(limit));
    return request<Page<FlatEvent>>(`/v1/sessions/${enc(id)}/events?${p.toString()}`);
  },
  visit: (id: string) => request<VisitDetail>(`/v1/visits/${enc(id)}`),

  // Requested API additions (see README "API additions requested")
  share: (id: string, expiresInHours: number, startMs: number) => request<ShareLink>(`/v1/sessions/${enc(id)}/share`, { method: "POST", body: { expiresInHours, startMs } }),
  shares: (id: string) => request<{ items: SessionShareLink[] }>(`/v1/sessions/${enc(id)}/share`),
  revokeShare: (id: string, linkId: string) => request<void>(`/v1/sessions/${enc(id)}/share/${enc(linkId)}`, { method: "DELETE" }),
  shared: (token: string) => request<SharedSession>(`/share/${enc(token)}`, { public: true }),
  sharedChunk: (token: string, seq: number) => request<Chunk>(`/share/${enc(token)}/chunks/${seq}`, { public: true }),
  sharedEvents: (token: string) => request<{ items: FlatEvent[] }>(`/share/${enc(token)}/events?limit=5000`, { public: true }),
  sharedConversations: (token: string) => request<{ items: SharedConversationSummary[] }>(`/share/${enc(token)}/conversations`, { public: true }),
  sharedConversation: (token: string, id: string, cursor = "") =>
    request<Conversation>(`/share/${enc(token)}/conversations/${enc(id)}?limit=30${cursor ? `&cursor=${enc(cursor)}` : ""}`, { public: true }),
  exportStart: (id: string) => request<ExportJob>(`/v1/sessions/${enc(id)}/export`, { method: "POST", body: { format: "mp4" } }),
  exportStatus: (id: string, jobId: string) => request<ExportJob>(`/v1/sessions/${enc(id)}/export/${enc(jobId)}`),

  // -------------------------------------------------------------------------
  // First-run setup (public; every call carries the one-time bootstrap code)
  // -------------------------------------------------------------------------
  launch: {
    availability: () => request<{ open: boolean }>("/v1/public/availability", { public: true }),
    notify: (email: string, turnstileToken?: string) => request<{ ok: boolean }>("/v1/public/notify", { method: "POST", body: { email, turnstileToken }, public: true }),
  },
  setup: {
    status: () => request<SetupStatus>("/setup/status", { quiet401: true }),
    verify: (code: string) => request<{ ok: true }>("/setup/verify", { method: "POST", body: { code }, quiet401: true }),
    testEmail: (code: string, to: string, mail: SetupMail) => request<{ ok: true }>("/setup/test-email", { method: "POST", body: { code, to, mail }, quiet401: true }),
    lookupAddress: (code: string, address: PostalAddress) => request<AddressLookupResult>("/setup/address-lookup", { method: "POST", body: { code, address }, quiet401: true }),
    complete: (b: SetupCompleteRequest) => request<AuthResult>("/setup/complete", { method: "POST", body: b, quiet401: true }),
  },

  // -------------------------------------------------------------------------
  // Auth (public)
  // -------------------------------------------------------------------------
  auth: {
    signup: (b: SignupRequest) => request<AuthResult>("/auth/signup", { method: "POST", body: b, quiet401: true }),
    signupStart: (email: string, plan?: "free" | "team" | "business", turnstileToken?: string) =>
      request<{ ok: true }>("/auth/signup/start", { method: "POST", body: { email, plan, turnstileToken }, quiet401: true }),
    signupPending: (token: string) =>
      request<{ email: string; plan: "free" | "team" | "business" }>(`/auth/signup/pending?token=${enc(token)}`, { quiet401: true }),
    login: (b: LoginRequest) => request<AuthResult | MfaChallenge>("/auth/login", { method: "POST", body: b, quiet401: true }),
    mfaVerify: (b: MfaVerifyRequest) => request<AuthResult>("/auth/mfa/verify", { method: "POST", body: b, quiet401: true }),
    magicLink: (email: string, turnstileToken?: string) => request<{ ok: true }>("/auth/magic-link", { method: "POST", body: { email, turnstileToken }, quiet401: true }),
    magicLinkConsume: (token: string) => request<AuthResult>(`/auth/magic-link/consume?token=${enc(token)}`, { quiet401: true }),
    verifyEmail: (token: string) => request<AuthResult>("/auth/verify-email", { method: "POST", body: { token }, quiet401: true }),
    resendVerifyEmail: () => request<{ ok: true }>("/auth/verify-email/resend", { method: "POST", quiet401: true }),
    forgot: (email: string, turnstileToken?: string) => request<{ ok: true }>("/auth/password/forgot", { method: "POST", body: { email, turnstileToken }, quiet401: true }),
    reset: (token: string, password: string) => request<{ ok: true }>("/auth/password/reset", { method: "POST", body: { token, password }, quiet401: true }),
    ssoLookup: (email: string) => request<{ connectionId?: string; requireSso: boolean }>(`/auth/sso/lookup?email=${enc(email)}`, { quiet401: true }),
    providers: () => request<{ social: Array<"google" | "microsoft" | "github">; passkeys: boolean; magicLink: boolean }>("/auth/providers", { public: true, quiet401: true }),
    oauthStartUrl: (provider: "google" | "microsoft" | "github", next: string) => `${API_URL}/auth/oauth/${provider}/start?next=${enc(next)}`,
    ssoStartUrl: (connectionId: string, next: string) => `${API_URL}/auth/sso/${enc(connectionId)}/start?next=${enc(next)}`,
    passkeyLoginOptions: (challengeId?: string) => request<PasskeyRequestOptions>("/auth/passkeys/login/options", { method: "POST", body: { challengeId }, quiet401: true }),
    passkeyLoginVerify: (body: { challengeId: string; credential: unknown }) => request<AuthResult>("/auth/passkeys/login/verify", { method: "POST", body, quiet401: true }),
    /** Who the invitation is for, so the accept page can create the account with the right email. */
    peekInvite: (token: string) => request<{ email: string; role: Role; orgName: string; expiresAt: string }>(`/auth/invitations/${enc(token)}`, { quiet401: true }),
    acceptInvite: (token: string, body?: { email?: string; password?: string }) => request<AuthResult>(`/auth/invitations/${enc(token)}/accept`, { method: "POST", body: body ?? {}, quiet401: true }),
    logout: () => request<void>("/auth/logout", { method: "POST", quiet401: true }),
  },

  // -------------------------------------------------------------------------
  // Me
  // -------------------------------------------------------------------------
  me: {
    get: (quiet = true) => request<Me>("/v1/me", { quiet401: quiet }),
    patch: (b: { name?: string; avatarUrl?: string }) => request<User>("/v1/me", { method: "PATCH", body: b }),
    password: (current: string, next: string) => request<void>("/v1/me/password", { method: "POST", body: { current, next } }),
    mfa: () => request<MfaStatus>("/v1/me/mfa"),
    totpStart: () => request<{ secret: string; otpauthUrl: string; qrSvg: string }>("/v1/me/mfa/totp/start", { method: "POST" }),
    totpConfirm: (code: string) => request<{ recoveryCodes: string[] }>("/v1/me/mfa/totp/confirm", { method: "POST", body: { code } }),
    totpDisable: (proof: FactorProof) => request<void>("/v1/me/mfa/totp", { method: "DELETE", body: proof }),
    passkeyRegisterOptions: () => request<PasskeyCreationOptions>("/v1/me/passkeys/register/options", { method: "POST" }),
    passkeyRegisterVerify: (challengeId: string, credential: unknown, name: string) =>
      request<Passkey>("/v1/me/passkeys/register/verify", { method: "POST", body: { challengeId, credential, name } }),
    passkeyStepUpOptions: () => request<PasskeyRequestOptions>("/v1/me/passkeys/step-up/options", { method: "POST" }),
    passkeyDelete: (id: string, proof: FactorProof) => request<void>(`/v1/me/passkeys/${enc(id)}`, { method: "DELETE", body: proof }),
    sessions: () => request<LoginSession[]>("/v1/me/sessions"),
    revokeSession: (id: string) => request<void>(`/v1/me/sessions/${enc(id)}`, { method: "DELETE" }),
    revokeOtherSessions: () => request<void>("/v1/me/sessions", { method: "DELETE" }),
  },

  // -------------------------------------------------------------------------
  // Orgs
  // -------------------------------------------------------------------------
  orgs: {
    list: () => request<Organization[]>("/v1/orgs"),
    create: (name: string, slug: string) => request<Organization>("/v1/orgs", { method: "POST", body: { name, slug } }),
    get: (orgId: string) => request<Organization>(`/v1/orgs/${enc(orgId)}`),
    patch: (orgId: string, b: OrgSettingsPatch) => request<Organization>(`/v1/orgs/${enc(orgId)}`, { method: "PATCH", body: b }),
    lookupAddress: (orgId: string, address: PostalAddress) => request<AddressLookupResult>(`/v1/orgs/${enc(orgId)}/address/lookup`, { method: "POST", body: address }),
    members: (orgId: string) => request<Member[]>(`/v1/orgs/${enc(orgId)}/members`),
    setRole: (orgId: string, userId: string, role: Role) => request<Member>(`/v1/orgs/${enc(orgId)}/members/${enc(userId)}`, { method: "PATCH", body: { role } }),
    renameMember: (orgId: string, userId: string, name: string) => request<{ ok: true }>(`/v1/orgs/${enc(orgId)}/members/${enc(userId)}`, { method: "PATCH", body: { name } }),
    removeMember: (orgId: string, userId: string) => request<void>(`/v1/orgs/${enc(orgId)}/members/${enc(userId)}`, { method: "DELETE" }),
    invitations: (orgId: string) => request<Invitation[]>(`/v1/orgs/${enc(orgId)}/invitations`),
    invite: (orgId: string, b: { email: string; role: Role; projectIds?: string[] }) => request<Invitation>(`/v1/orgs/${enc(orgId)}/invitations`, { method: "POST", body: b }),
    revokeInvite: (orgId: string, id: string) => request<void>(`/v1/orgs/${enc(orgId)}/invitations/${enc(id)}`, { method: "DELETE" }),
    projects: (orgId: string) => request<ProjectFull[]>(`/v1/orgs/${enc(orgId)}/projects`),
    browserKeys: (orgId: string) => request<{ keys: BrowserKey[] }>(`/v1/orgs/${enc(orgId)}/browser-keys`).then((r) => r.keys),
    deletions: (orgId: string) => request<{ deletions: Deletion[] }>(`/v1/orgs/${enc(orgId)}/deletions`).then((r) => r.deletions),
    createProject: (orgId: string, b: { name: string; privacyMode?: string; retentionDays?: number; allowedOrigins?: string[] }) =>
      request<ProjectFull>(`/v1/orgs/${enc(orgId)}/projects`, { method: "POST", body: b }),
    tokens: (orgId: string) => request<ApiToken[]>(`/v1/orgs/${enc(orgId)}/tokens`),
    createToken: (orgId: string, b: { name: string; scopes: string[]; projectId?: string; expiresAt?: string }) => request<ApiTokenCreated>(`/v1/orgs/${enc(orgId)}/tokens`, { method: "POST", body: b }),
    deleteToken: (orgId: string, id: string) => request<void>(`/v1/orgs/${enc(orgId)}/tokens/${enc(id)}`, { method: "DELETE" }),
    audit: (orgId: string, q: { cursor?: string; limit?: number; action?: string; actor?: string }) => {
      const p = new URLSearchParams();
      if (q.cursor) p.set("cursor", q.cursor);
      p.set("limit", String(q.limit ?? 50));
      if (q.action) p.set("action", q.action);
      if (q.actor) p.set("actor", q.actor);
      return request<Page<AuditEntry>>(`/v1/orgs/${enc(orgId)}/audit?${p.toString()}`);
    },
    sso: (orgId: string) => request<SsoConnection[]>(`/v1/orgs/${enc(orgId)}/sso`),
    createSso: (orgId: string, b: SsoConnectionCreate) => request<SsoConnection>(`/v1/orgs/${enc(orgId)}/sso`, { method: "POST", body: b }),
    patchSso: (orgId: string, id: string, b: Partial<SsoConnectionCreate>) => request<SsoConnection>(`/v1/orgs/${enc(orgId)}/sso/${enc(id)}`, { method: "PATCH", body: b }),
    deleteSso: (orgId: string, id: string) => request<void>(`/v1/orgs/${enc(orgId)}/sso/${enc(id)}`, { method: "DELETE" }),
    // Requested API addition
    usage: async (orgId: string) => {
      // Server returns meters keyed by name (api-billing.ts) plus `metersList`; normalize to an array.
      const raw = await request<UsageReport & { metersList?: UsageMeter[]; meters: UsageMeter[] | Record<string, UsageMeter> }>(`/v1/orgs/${enc(orgId)}/usage`);
      const meters = Array.isArray(raw.meters) ? raw.meters : raw.metersList ?? Object.entries(raw.meters ?? {}).map(([meter, m]) => ({ ...(m as UsageMeter), meter: (m as UsageMeter).meter ?? meter }));
      return { ...raw, meters } as UsageReport;
    },
  },

  projects: {
    get: (projectId: string) => request<ProjectFull>(`/v1/projects/${enc(projectId)}`),
    install: (projectId: string) => request<ProjectInstall>(`/v1/projects/${enc(projectId)}/install`),
    patch: (projectId: string, b: ProjectPatch) => request<ProjectFull>(`/v1/projects/${enc(projectId)}`, { method: "PATCH", body: b }),
    createBrowserKey: (projectId: string, name: string) => request<NewBrowserKey>(`/v1/projects/${enc(projectId)}/browser-keys`, { method: "POST", body: { name } }),
    revokeBrowserKey: (projectId: string, keyId: string) => request<void>(`/v1/projects/${enc(projectId)}/browser-keys/${enc(keyId)}`, { method: "DELETE" }),
    revoke: (projectId: string) => request<Deletion>(`/v1/projects/${enc(projectId)}`, { method: "DELETE" }),
  },
};

// ---------------------------------------------------------------------------
// Requested API additions (not yet in packages/protocol; see README)
// ---------------------------------------------------------------------------

export interface UsageMeter {
  meter: "stored_sessions" | "ai_credits_usd" | "deep_analyses" | "canvas_minutes" | "live_minutes";
  used: number;
  included: number;
  overageUnits?: number;
  overageUsd?: number;
  overageUnitPrice?: number;
  projectedMonthEnd: number;
  hardCap?: number;
}
export interface UsageReport {
  periodStart: string;
  periodEnd: string;
  plan: Organization["plan"];
  meters: UsageMeter[];
}

// Minimal JSON shapes for WebAuthn options (base64url strings), as returned by
// the api: browser options under `publicKey`, plus the one-shot challenge id
// that the matching verify call has to hand back.
export interface PublicKeyCredentialCreationOptionsJSONLike {
  challenge: string;
  rp: { name: string; id?: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}
export interface PublicKeyCredentialRequestOptionsJSONLike {
  challenge: string;
  rpId?: string;
  timeout?: number;
  allowCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  userVerification?: UserVerificationRequirement;
}
/** Fresh proof required before a sign-in method is removed. */
export interface FactorProof {
  password?: string;
  code?: string;
  challengeId?: string;
  credential?: unknown;
}

export interface PasskeyCreationOptions {
  challengeId: string;
  publicKey: PublicKeyCredentialCreationOptionsJSONLike;
}
export interface PasskeyRequestOptions {
  challengeId: string;
  publicKey: PublicKeyCredentialRequestOptionsJSONLike;
}

/** A delete the server accepted and is still finishing. */
export interface Deletion {
  id: string;
  kind: "session" | "sessions" | "backend" | "app";
  targetId: string;
  targetName: string;
  status: "running" | "done" | "failed";
  error?: string;
  createdAt: string;
  finishedAt?: string;
  /** Set when the delete covers [rangeFrom, rangeTo) instead of everything. */
  rangeFrom?: string;
  rangeTo?: string;
}

function windowQuery(w?: { from?: string; to?: string }): string {
  const p = new URLSearchParams();
  if (w?.from) p.set("from", w.from);
  if (w?.to) p.set("to", w.to);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const queryKeys = {
  project: ["project"] as const,
  environments: (project?: string) => ["environments", project ?? ""] as const,
  sessions: (q: SessionsQuery) => ["sessions", q] as const,
  session: (id: string) => ["session", id] as const,
  sessionActivity: (id: string) => ["session-activity", id] as const,
  events: (id: string) => ["events", id] as const,
  visit: (id: string) => ["visit", id] as const,
  me: ["me"] as const,
  orgs: ["orgs"] as const,
  org: (id: string) => ["org", id] as const,
  members: (id: string) => ["members", id] as const,
  invitations: (id: string) => ["invitations", id] as const,
  projects: (id: string) => ["projects", id] as const,
  deletions: (id: string) => ["deletions", id] as const,
  browserKeys: (id: string) => ["browserKeys", id] as const,
  projectPicker: (id: string) => ["projectPicker", id] as const,
  projectFull: (id: string) => ["projectFull", id] as const,
  tokens: (id: string) => ["tokens", id] as const,
  audit: (id: string, f: unknown) => ["audit", id, f] as const,
  sso: (id: string) => ["sso", id] as const,
  usage: (id: string) => ["usage", id] as const,
  mfa: ["mfa"] as const,
  loginSessions: ["loginSessions"] as const,
  shared: (t: string) => ["shared", t] as const,
  sessionShares: (id: string) => ["sessionShares", id] as const,
};
